import {Component, OnDestroy, OnInit} from '@angular/core';
import {
	distinctUntilChanged,
	finalize,
	Observable,
	of,
	Subscription,
	switchMap,
	tap
} from 'rxjs';
import {EXTENSION_URL} from 'src/app/models/home/navigation.model';
import {ApplicationDetailData, ApplicationDetailService} from 'src/app/services/front/application-detail.service';
import {debounceTime, filter, map} from 'rxjs/operators';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {SnackbarService} from 'src/app/services/front/snackbar.service';
import {Router} from '@angular/router';
import {
	ApplicationInstance,
	ApplicationUsageUrl
} from 'src/app/services/model/new-application.model';
import {NewApplicationService} from 'src/app/services/back/new-application.service';
import {RightSliderService} from "../../../../../../services/front/right-slider.service";
import {suffixDomainValidator} from "../../../../../../utils/forms.utils";

@Component({
	selector: 'app-application-setting-usage',
	standalone: false,
	templateUrl: './application-setting-usage.component.html',
	styleUrls: ['../application-settings.component.scss']
})
export class ApplicationSettingUsageComponent implements OnInit, OnDestroy {

	tenantId: string;
	isEditor: boolean;
	application: ApplicationInstance;

	_initializing: boolean;
	_loading: boolean;
	initialized = false;

	subscription: Subscription = new Subscription();

	desktopApplicationNames: string[] = [];
	usageUrls: ApplicationUsageUrl[] = [];

	private _updating = false;

	// Form controls
	usageActivatedControl: FormControl<boolean> = new FormControl<boolean>(true, { nonNullable: true });
	desktopApplicationNamesControl: FormArray<FormControl<string>> = new FormArray<FormControl<string>>([], { updateOn: "blur" });
	usageUrlsControl: FormArray<FormGroup<UsageUrlForm>> = new FormArray<FormGroup<UsageUrlForm>>([], { updateOn: "blur" });

	displayExtensionControl: FormControl<boolean> = new FormControl<boolean>(true, { nonNullable: true });

	constructor(private applicationDetailService: ApplicationDetailService,
				private applicationService: NewApplicationService,
				private translate: TranslateService,
				private snackBar: SnackbarService,
				private router: Router,
				private rightSliderService: RightSliderService) {
	}

	ngOnInit() {
		this.subscription.add(this.applicationDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscription.add(this.applicationDetailService.getApplicationDetailDataChanges()
			.pipe(tap(data => this.setApplicationDetailData(data)))
			.subscribe(() => this.initialize()));

		this.subscription.add(this.usageActivatedControl.valueChanges
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				switchMap(isActivated => this.applicationService.updateApplicationUsageActivated(this.tenantId, this.application.applicationId, { usageActivated: isActivated })),
				tap(updateSucceed => this.displayUpdatedSucceed(updateSucceed)),
				tap(() => this.applicationDetailService.refreshApplicationInstance()),
			).subscribe());

		this.subscription.add(this.displayExtensionControl.valueChanges
			.pipe(
				distinctUntilChanged(),
				switchMap(displayedInExtension => this.applicationService.updateApplicationsDisplayed(this.tenantId,  [{ id: this.application.applicationId, displayed: displayedInExtension }])),
				tap(updateSucceed => this.displayUpdatedSucceed(updateSucceed)),
				tap(() => this.applicationDetailService.refreshApplicationInstance()),
			)
			.subscribe());

		this.subscription.add(this.desktopApplicationNamesControl.valueChanges
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				filter(() => !this._updating),
				map(names => names.filter(name => name.trim() !== '')),
				filter(names => names.length !== this.desktopApplicationNames.length || names.some((name, index) => name !== this.desktopApplicationNames[index])),
				tap(() => this.autoActivateUsageIfNeeded()),
				tap(() => this._updating = true),
				switchMap(values => this.applicationService.updateDesktopApplicationNames(this.tenantId, this.application.applicationId, { desktopApplicationNames: values })),
				tap(updateSucceed => this.displayUpdatedSucceed(updateSucceed)),
				tap(() => {
					this._updating = false;
					this.applicationDetailService.refreshApplicationInstance();
				})
			).subscribe());

		this.subscription.add(this.usageUrlsControl.valueChanges
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				filter(() => !this._updating),
				map(() => {
					return this.usageUrlsControl.controls
						.filter(group => group.valid && group.get('domain')?.value?.trim())
						.map(group => ({
							id: group.get('id')?.value || null,
							domain: group.get('domain')?.value?.trim(),
							path: group.get('path')?.value || null
						}));
				}),
				tap(() => this.autoActivateUsageIfNeeded()),
				filter(values => this.usageUrlsChanged(values)),
				tap(() => this._updating = true),
				switchMap(values => this.applicationService.updateApplicationUsageUrls(this.tenantId, this.application.applicationId, {
					urls: values.map(partial => {
						return { id: partial.id || null, domain: partial.domain!, path: partial.path || null };
					})
				})),
				tap(updateSucceed => this.displayUpdatedSucceed(updateSucceed)),
				tap(() => {
					this._updating = false;
					this.applicationDetailService.refreshApplicationInstance();
				})
			).subscribe());
	}

	initialize(): void {
		this.subscription.add(this.switchLoading()
			.pipe(
				switchMap(() => this.applicationService.getApplicationUsageUrls(this.tenantId, this.application.applicationId)),
				tap((urls) => this.setDefaultData(urls, this.initialized)),
				finalize(() => this.switchLoading()))
			.subscribe(() => this.initialized = true));
	}

	private setApplicationDetailData(data: ApplicationDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.application = data.instance;
		this.displayExtensionControl.setValue(this.application.displayedInExtension, { emitEvent: false });
	}

	private setDefaultData(urls: ApplicationUsageUrl[], preserveEmptyRows: boolean = false): void {
		if (!preserveEmptyRows || !this.initialized) {
			this.usageUrls = urls;
			this.usageUrlsControl.clear();
			if (this.usageUrlsControl.length <= 0 && this.usageUrls.length <= 0) {
				this.usageUrlsControl.push(this.createUrlFormGroup(), { emitEvent: false });
			} else {
				this.usageUrls.forEach(url => this.usageUrlsControl.push(this.createUrlFormGroup(url), { emitEvent: false }));
			}

			this.usageActivatedControl.setValue(this.application.usageActivated, { emitEvent: false });

			this.desktopApplicationNames = this.application.desktopApplicationNames;
			this.desktopApplicationNamesControl.clear();
			if (this.desktopApplicationNames.length <= 0) {
				this.desktopApplicationNamesControl.push(new FormControl('', { nonNullable: true }), { emitEvent: false });
			} else {
				this.desktopApplicationNames.forEach(applicationName =>
					this.desktopApplicationNamesControl.push(new FormControl(applicationName, { nonNullable: true }), { emitEvent: false }));
			}

			const hasValidDesktopAppName = this.desktopApplicationNamesControl.controls
				.some(control => control.value.trim() !== '');

			const hasValidUsageUrl = this.usageUrlsControl.controls
				.some(group => {
					const domain = group.get('domain')?.value?.trim();
					return !!domain;
				});

			if (!hasValidDesktopAppName && !hasValidUsageUrl)
				this.usageActivatedControl.disable({emitEvent: false});
		} else {
			this.usageActivatedControl.setValue(this.application.usageActivated, { emitEvent: false });

			this.usageUrls = urls;
			const existingUrlIds = new Set(urls.map(url => url.id));

			for (let i = 0; i < this.usageUrlsControl.length; i++) {
				const control = this.usageUrlsControl.at(i);
				const id = control.get('id')?.value;

				if (id && existingUrlIds.has(id)) {
					const url = urls.find(u => u.id === id);
					if (url) {
						control.patchValue({
							domain: url.domain,
							path: url.path
						}, { emitEvent: false });
					}
				}
			}

			// Update desktop application names without clearing the form
			this.desktopApplicationNames = this.application.desktopApplicationNames;

			// Update existing names controls
			let nameIndex = 0;
			for (let i = 0; i < this.desktopApplicationNamesControl.length; i++) {
				// Only update controls that match a server-side name
				if (nameIndex < this.desktopApplicationNames.length) {
					const existingValue = this.desktopApplicationNamesControl.at(i).value;
					// Only update non-empty controls to preserve user edits
					if (existingValue) {
						this.desktopApplicationNamesControl.at(i).setValue(
							this.desktopApplicationNames[nameIndex],
							{ emitEvent: false }
						);
						nameIndex++;
					}
				}
			}
		}
		this.validateAndUpdateUsageControlState();
	}

	private validateAndUpdateUsageControlState(): void {
		const hasValidDesktopAppName = this.desktopApplicationNamesControl.controls
			.some(control => control.value?.trim() !== '');

		const hasValidUsageUrl = this.usageUrlsControl.controls
			.some(group => {
				const domain = group.get('domain')?.value?.trim();
				return group.valid && !!domain;
			});

		if (hasValidDesktopAppName || hasValidUsageUrl) {
			if (this.usageActivatedControl.disabled) {
				this.usageActivatedControl.enable({ emitEvent: false });
			}
		} else {
			if (this.usageActivatedControl.enabled) {
				this.usageActivatedControl.setValue(false, { emitEvent: false });
				this.usageActivatedControl.disable({ emitEvent: false });
			}
		}
	}

	usageUrlsChanged(values:  Partial<{ id: string | null, domain: string, path: string | null }>[]): boolean {
		if (values.length !== this.usageUrls.length) {
			return true;
		}

		for (let i = 0; i < values.length; i++) {
			const value = values[i];
			const url = this.usageUrls[i];

			if (value.id !== url.id || value.domain !== url.domain || value.path !== url.path) {
				return true;
			}
		}

		return false;
	}

	private autoActivateUsageIfNeeded(): void {
		const hasValidDesktopAppName = this.desktopApplicationNamesControl.controls
			.some(control => control.value.trim() !== '');

		const hasValidUsageUrl = this.usageUrlsControl.controls
			.some(group => group.valid && group.get('domain')?.value?.trim() && group.get('domain')?.valid);

		if (hasValidDesktopAppName || hasValidUsageUrl) {
			if (!this.usageActivatedControl.value) {
				this.usageActivatedControl.setValue(true);
			}
			this.usageActivatedControl.enable({ emitEvent: false });
		} else {
			if (this.usageActivatedControl.value) {
				this.usageActivatedControl.setValue(false);
			}
			this.usageActivatedControl.disable({ emitEvent: false });
		}
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackBar.show(this.translate.instant('page.application.detail.update.success'));
		} else {
			this.snackBar.show(this.translate.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	goToSettings() {
		this.router.navigate([EXTENSION_URL], { queryParams: { tenant: this.tenantId } }).then(() => this.rightSliderService.close());
	}

	removeDesktopApplicationName(index: number): void {
		this.desktopApplicationNamesControl.removeAt(index);
	}

	addNewDesktopApplicationName(): void {
		this.desktopApplicationNamesControl.push(new FormControl('', { nonNullable: true }), { emitEvent: false });
	}

	private createUrlFormGroup(url?: ApplicationUsageUrl): FormGroup {
		const formGroup = new FormGroup({
			'id': new FormControl(url?.id || null),
			'domain': new FormControl(url?.domain || '', { validators: suffixDomainValidator() }),
			'path': new FormControl(url?.path || null)
		});

		formGroup.get('domain')?.valueChanges.pipe(
			distinctUntilChanged()
		).subscribe(domainValue => {
			if (!domainValue || domainValue.trim() === '') {
				formGroup.get('path')?.setValue(null, { emitEvent: false });
			}
		});

		return formGroup;
	}

	addNewUrl(): void {
		this.usageUrlsControl.push(this.createUrlFormGroup(), { emitEvent: false });
	}

	removeUrl(index: number): void {
		this.usageUrlsControl.removeAt(index);
	}

	private switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}

interface UsageUrlForm {
	id: FormControl<string | null>;
	domain: FormControl<string>;
	path: FormControl<string | null>;
}

